/**
 * Origin Private File System (OPFS) manager for storing files
 * that need to be uploaded. Files are stored in OPFS to persist
 * across browser sessions and page reloads.
 */

let rootDirPromise: Promise<FileSystemDirectoryHandle> | null = null

/**
 * Get the root directory handle for OPFS
 */
async function getRootOpfsDirectory(): Promise<FileSystemDirectoryHandle> {
  if (!rootDirPromise) {
    rootDirPromise = navigator.storage.getDirectory()
  }
  return rootDirPromise
}

async function getUploadsDirectory(props: { create: boolean }): Promise<FileSystemDirectoryHandle> {
  const root = await getRootOpfsDirectory()
  return root.getDirectoryHandle('uploads', props)
}

/**
 * Navigate to a file's directory and return both the directory handle and filename
 */
async function navigateToFile(
  opfsPath: string,
): Promise<{ dir: FileSystemDirectoryHandle; fileName: string } | null> {
  const root = await getRootOpfsDirectory()
  const pathParts = opfsPath.split('/')

  let currentDir: FileSystemDirectoryHandle = root

  // Navigate to the file's directory
  const dirNames = pathParts.slice(0, -1)
  for (const dirName of dirNames) {
    if (!dirName) continue
    currentDir = await currentDir.getDirectoryHandle(dirName)
  }

  // Get the filename
  const fileName = pathParts.at(-1)
  if (!fileName) return null

  return { dir: currentDir, fileName }
}

/**
 * Store a file in OPFS and return its path
 */
export async function storeFile(file: File): Promise<string> {
  const uploadId = crypto.randomUUID()
  const uploadsDir = await getUploadsDirectory({ create: true })

  // Create a unique directory for this upload
  const uploadDir = await uploadsDir.getDirectoryHandle(uploadId, { create: true })

  // Create file with original name
  const fileHandle = await uploadDir.getFileHandle(file.name, { create: true })

  // Write file content
  const writable = await fileHandle.createWritable()
  await writable.write(file)
  await writable.close()

  // Return the path for retrieval
  return `uploads/${uploadId}/${file.name}`
}

/**
 * Retrieve a file from OPFS
 */
export async function getFile(opfsPath: string): Promise<File | null> {
  try {
    const result = await navigateToFile(opfsPath)
    if (!result) return null

    const { dir, fileName } = result
    const fileHandle = await dir.getFileHandle(fileName)
    return await fileHandle.getFile()
  } catch {
    return null
  }
}

/**
 * Delete a file from OPFS
 */
export async function deleteFile(opfsPath: string): Promise<boolean> {
  try {
    const result = await navigateToFile(opfsPath)
    if (!result) return false

    const { dir, fileName } = result
    await dir.removeEntry(fileName)

    // Try to clean up empty directories
    if (opfsPath.split('/').length >= 3) {
      const uploadId = opfsPath.split('/')[1]
      if (uploadId) {
        try {
          const uploadsDir = await getUploadsDirectory({ create: false })
          await uploadsDir.removeEntry(uploadId, { recursive: true })
        } catch {
          // Technically directory might not be empty or might not exist, but we don't care
        }
      }
    }

    return true
  } catch {
    return false
  }
}

/**
 * Clean up old files that are no longer needed
 */
export async function cleanupOldFiles(keepPaths: Set<string>): Promise<void> {
  try {
    const uploadsDir = await getUploadsDirectory({ create: false })

    // Iterate through all upload directories
    for await (const [uploadId, handle] of uploadsDir) {
      if (handle.kind === 'directory') {
        let hasKeptFiles = false

        // Check if any files in this directory should be kept
        const uploadDir = await uploadsDir.getDirectoryHandle(uploadId)

        for await (const [fileName] of uploadDir) {
          const path = `uploads/${uploadId}/${fileName}`
          if (keepPaths.has(path)) {
            hasKeptFiles = true
            break
          }
        }

        // If no files should be kept, remove the entire directory
        if (!hasKeptFiles) {
          await uploadsDir.removeEntry(uploadId, { recursive: true })
        }
      }
    }
  } catch {
    // Silently handle errors during cleanup
  }
}
